import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  AlertTitle,
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useEffect, useState } from "react";
import { colorGrid } from "../../theme";
import { useUserData } from "../../user";
import { useExchangeStatus } from "../../utils/contexts/entities/exchanges/exchangeStatusContext";
import {
  formatAndSortVolumesForLineChart,
  getLatestVolume,
  getTradingPairs,
} from "../../utils/entity/entity";
import { fillEmptyDates } from "../../utils/graphs/graphs";
import { SERIES_COLORS } from "../global/charts/chartTheme";
import UptimeStrip, { UptimeLegend } from "../global/charts/UptimeStrip";
import {
  formatSince,
  isCheckFailing,
  isChecksPaused,
} from "../../utils/entity/checkHealth";
import PauseCheckButton from "../global/PauseCheckButton";
import CustomButton from "../global/CustomButton";
import CustomCard from "../global/CustomCard";
import LineChart from "../global/LineChart";
import TruncateWords from "../global/TruncatedWords";


function createChartData(entity) {
  const chartData = [];
  if (entity.baseVolumes.length > 0) {
    const dataBase = formatAndSortVolumesForLineChart(entity, "base");
    chartData.push({
      id: "baseVolume",
      data: dataBase.map((dataItem) => ({
        y: dataItem.y,
        x: dataItem.formattedDate,
      })),
      visible: true,
      color: SERIES_COLORS[0],
      name: "Base volume",
    });
  }

  if (entity.targetVolumes.length > 0) {
    const dataTarget = formatAndSortVolumesForLineChart(entity, "target");
    chartData.push({
      id: "targetVolume",
      data: dataTarget.map((dataItem) => ({
        y: dataItem.y,
        x: dataItem.formattedDate,
      })),
      visible: true,
      color: SERIES_COLORS[1],
      name: "Target volume",
    });
  }
  return chartData;
}

const ExchangeDetails = ({
  entityInit,
  poolsInit,
  countriesInit,
  onEdit,
  exchangeInfo,
}) => {
  const [entity, setEntity] = useState(entityInit);
  const [pools, setPools] = useState([]);
  const [countries, setCountries] = useState([]);
  // The cards in a row stretch to the tallest one, so an expanded list
  // (countries, trading pairs) grows its card instead of being cut off
  const [cardSx, setCardSx] = useState({
    height: "100%",
    minHeight: "200px",
  });
  const [activeStakeSum, setActiveStakeSum] = useState(0);
  const dataLineChart = fillEmptyDates(createChartData(entity));
  const userData = useUserData();
  const {
    globalUpdate: exchangeDEWISUpdate,
    exchangeUpdate,
    joinExchangeRoom,
    leaveExchangeRoom,
  } = useExchangeStatus();

  const initialDepositStatus = entity?.exchangeStatus
    ? entity?.exchangeStatus?.depositAllowed
      ? "Online"
      : "Offline"
    : "No Data";
  const initialWithdrawalStatus = entity?.exchangeStatus
    ? entity?.exchangeStatus?.withdrawalAllowed
      ? "Online"
      : "Offline"
    : "No Data";

  const initialFailScore = entity?.exchangeStatus
    ? entity?.exchangeStatus?.failScore
    : 0;

  const [currentDepositStatus, setCurrentDepositStatus] =
    useState(initialDepositStatus);
  const [currentWithdrawalStatus, setCurrentWithdrawalStatus] = useState(
    initialWithdrawalStatus
  );
  const [currentFailScore, setCurrentFailScore] = useState(initialFailScore);
  const checkFailing = isCheckFailing(entity?.exchangeStatus);
  const checksPaused = isChecksPaused(entity?.exchangeStatus);
  // While the check is failing the last known status may be outdated
  const statusChip = (status) =>
    checksPaused
      ? { label: "Paused", color: colorGrid["gray"] }
      : checkFailing
      ? { label: "Check failing", color: colorGrid["yellow"] }
      : {
          label: status,
          color:
            colorGrid[
              status === "No Data"
                ? "gray"
                : status === "Online"
                ? "green"
                : "red"
            ],
        };

  const handleChecksPausedChange = (health) => {
    setEntity((prevValue) => ({
      ...prevValue,
      exchangeStatus: {
        ...prevValue.exchangeStatus,
        checksPausedAt: health.checksPausedAt,
        checkErrorCount: health.checkErrorCount,
        checkFailingAlertSent: health.checkFailingAlertSent,
      },
    }));
  };

  function handleEdit(mode) {
    onEdit(mode);
  }

  useEffect(() => {
    joinExchangeRoom(entity.slug);

    return () => {
      leaveExchangeRoom(entity.slug);
    };
  }, [entity.slug, joinExchangeRoom, leaveExchangeRoom]);

  useEffect(() => {
    if (
      exchangeUpdate &&
      entity.slug === exchangeUpdate.exchangeSlug &&
      exchangeUpdate.failScore >= 0
    ) {
      setCurrentFailScore(exchangeUpdate.failScore);
    }
  }, [exchangeUpdate]);

  useEffect(() => {
    if (poolsInit && poolsInit.length > 0) {
      setPools(poolsInit);
      const totalActiveStake =
        poolsInit.reduce(
          (sum, pool) => sum + parseFloat(pool.activeStake || "0"),
          0
        ) / Math.pow(10, 6);

      if (totalActiveStake > 0) {
        const formattedActiveStake = totalActiveStake.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        setActiveStakeSum(formattedActiveStake);
        setCardSx((prevValues) => ({
          ...prevValues,
          minHeight: "220px",
        }));
      }
    }
  }, [poolsInit]);

  useEffect(() => {
    if (countriesInit && countriesInit.length > 0) {
      setCountries(countriesInit);
    }
  }, [countriesInit]);

  useEffect(() => {
    if (
      exchangeDEWISUpdate &&
      exchangeDEWISUpdate.statusUpdate &&
      entity.slug === exchangeDEWISUpdate.exchangeSlug
    ) {
      setCurrentDepositStatus(
        exchangeDEWISUpdate.statusUpdate.depositAllowed ? "Online" : "Offline"
      );
      setCurrentWithdrawalStatus(
        exchangeDEWISUpdate.statusUpdate.withdrawalAllowed
          ? "Online"
          : "Offline"
      );
      setEntity((prevValue) => {
        return {
          ...prevValue,
          exchangeStatus: {
            ...prevValue.exchangeStatus,
            statusChanges: [
              ...(prevValue.exchangeStatus?.statusChanges || []),
              ...exchangeDEWISUpdate.changedDEWIs,
            ],
          },
        };
      });
    }
  }, [exchangeDEWISUpdate]);


  return (
    <Grid container spacing={3}>
      {/* GENERAL CARD */}
      <Grid item xs={12} md={6} lg={4}>
        <CustomCard cardHeader={"General"} sx={cardSx}>
          <Grid container>
            <Grid item xs={6} alignItems={"center"}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                Base volume
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {entity && getLatestVolume(entity, "base")}
            </Grid>

            {entity && parseFloat(activeStakeSum) > 0 && (
              <>
                <Grid item xs={6} alignItems={"center"}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    Active Stake
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  {activeStakeSum}
                </Grid>
              </>
            )}

            <Grid item xs={6} alignItems={"center"}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                Target volume
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {entity && getLatestVolume(entity, "target")}
            </Grid>

            <Grid item xs={6} alignItems={"center"}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                Trust score
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {entity && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  height="100%"
                >
                  <Box
                    component="span"
                    bgcolor={
                      entity.trustScore
                        ? colorGrid[entity.trustScore].background
                        : colorGrid["gray"].background
                    }
                    color={
                      entity.trustScore
                        ? colorGrid[entity.trustScore].text
                        : colorGrid["gray"].text
                    }
                    borderRadius="4px"
                    height="24px"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    px={0.5}
                    py={0.2}
                    fontSize={".75rem"}
                    fontWeight={700}
                  >
                    {exchangeInfo &&
                    exchangeInfo.trust_score !== undefined &&
                    exchangeInfo.trust_score !== null ? (
                      exchangeInfo.trust_score
                    ) : entity && entity.trustScore ? (
                      entity.trustScore
                    ) : (
                      <>No Data</>
                    )}
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid item xs={6} alignItems={"center"}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                Country
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {exchangeInfo && exchangeInfo.country
                ? exchangeInfo.country
                : "No information"}
            </Grid>

            <Grid item xs={6} alignItems={"center"}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                Trading pairs
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {entity && (
                <TruncateWords
                  words={getTradingPairs(entity)}
                  separator={" | "}
                  displayCount={4}
                  sx={{
                    fontSize: "0.85rem",
                    fontFamily: "Public Sans, sans-serif",
                  }}
                />
              )}
            </Grid>
          </Grid>
        </CustomCard>
      </Grid>

      {/* INTEGRATION CARD */}

      <Grid item xs={12} md={6} lg={4}>
        <CustomCard
          sx={cardSx}
          cardHeader={
            <Grid container>
              <Grid item xs={6}>
                Integration
              </Grid>
              <Grid item xs={6}>
                {entity && (
                  <Box display="flex" height="100%" justifyContent={"end"}>
                    {userData.isAdmin && (
                      <CustomButton
                        type={"edit"}
                        buttonText={"Edit"}
                        onClick={() => handleEdit("integration")}
                        sx={{
                          borderRadius: "4px",
                          px: 0.5,
                          py: 0.2,
                          fontSize: "0.675rem",
                          height: "24px",
                        }}
                      />
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          }
        >
          <Grid container gap={1}>
            <Grid item xs={5} alignItems={"center"}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                Native assets
              </Typography>
            </Grid>
            <Grid item xs={5}>
              {entity?.hasNativeAssets ? "Yes" : "No"}
            </Grid>
            <Grid item xs={5} alignItems={"center"}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                Deposit status
              </Typography>
            </Grid>
            <Grid item xs={5}>
              {entity && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  height="100%"
                >
                  <Box
                    component="span"
                    bgcolor={statusChip(currentDepositStatus).color.background}
                    color={statusChip(currentDepositStatus).color.text}
                    borderRadius="4px"
                    height="24px"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    px={0.5}
                    py={0.2}
                    fontSize={".75rem"}
                    fontWeight={700}
                  >
                    {statusChip(currentDepositStatus).label}

                    {currentFailScore > 0 && (
                      <Tooltip
                        disableFocusListener
                        disableTouchListener
                        title={
                          <Box
                            sx={{
                              fontSize: "1rem",
                            }}
                          >
                            {currentFailScore.toFixed(1)}
                          </Box>
                        }
                      >
                        <WarningAmberIcon
                          sx={{
                            cursor: "pointer",
                            ml: "5%",
                            fontSize: ".75rem",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid item xs={5} alignItems={"center"}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                Withdrawal status
              </Typography>
            </Grid>
            <Grid item xs={5}>
              {entity && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  height="100%"
                >
                  <Box
                    component="span"
                    bgcolor={
                      statusChip(currentWithdrawalStatus).color.background
                    }
                    color={statusChip(currentWithdrawalStatus).color.text}
                    borderRadius="4px"
                    height="24px"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    px={0.5}
                    py={0.2}
                    fontSize={".75rem"}
                    fontWeight={700}
                  >
                    {statusChip(currentWithdrawalStatus).label}
                    {currentFailScore > 0 && (
                      <Tooltip
                        disableFocusListener
                        disableTouchListener
                        title={
                          <Box
                            sx={{
                              fontSize: "1rem",
                            }}
                          >
                            {currentFailScore.toFixed(1)}
                          </Box>
                        }
                      >
                        <WarningAmberIcon
                          sx={{
                            cursor: "pointer",
                            ml: "5%",
                            fontSize: ".75rem",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid item xs={5} alignItems={"center"}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                Type
              </Typography>
            </Grid>
            <Grid item xs={5}>
              {entity && "Exchange"}
            </Grid>
          </Grid>
        </CustomCard>
      </Grid>

      {/* SUPPORT CARD */}
      <Grid item xs={12} md={6} lg={4}>
        <CustomCard cardHeader={"Support"} sx={cardSx}>
          <Grid container>
            {/* <Grid item xs={6} alignItems={"center"}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                Networks
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {entity && "Mainnet | Preprod"}
            </Grid>
            <Grid item xs={6} alignItems={"center"}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                Components
              </Typography>
            </Grid>
            {entity &&
              ("cardano-node | rosetta | graphql".length > 20 ? (
                <Tooltip
                  title={
                    <h2 style={{ color: "lightblue" }}>
                      {"cardano-node | rosetta | graphql"}
                    </h2>
                  }
                >
                  <span
                    style={{ cursor: "pointer" }}
                  >{`${"cardano-node | rosetta | graphql".slice(
                    0,
                    25
                  )}...`}</span>
                </Tooltip>
              ) : (
                <span>{"cardano-node | rosetta | graphql"}</span>
              ))} */}

            <Grid item xs={6} alignItems={"center"}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                Centralized
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {entity && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  height="100%"
                >
                  <Box
                    component="span"
                    bgcolor={
                      entity.isCentralized
                        ? colorGrid["blue"].background
                        : colorGrid["gray"].background
                    }
                    color={
                      entity.isCentralized
                        ? colorGrid["blue"].text
                        : colorGrid["gray"].text
                    }
                    borderRadius="4px"
                    height="24px"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    px={0.5}
                    py={0.2}
                    fontSize={".75rem"}
                    fontWeight={700}
                  >
                    {entity.isCentralized !== (undefined || null) ? (
                      <>{entity.isCentralized ? "Yes" : "No"}</>
                    ) : (
                      "No Data"
                    )}
                  </Box>
                </Box>
              )}
            </Grid>
            {entity && countries.length > 0 && (
              <>
                <Grid item xs={6} alignItems={"center"}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    Supported countries
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TruncateWords
                    words={countries.map((country) => country.name)}
                    separator={" | "}
                    displayCount={2}
                    sx={{
                      fontSize: "0.85rem",
                      fontFamily: "Public Sans, sans-serif",
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </CustomCard>
      </Grid>
      {entity?.exchangeStatus && (
        <Grid item xs={12}>
          <CustomCard
            cardHeader={"Deposits & withdrawals"}
            sx={{ height: "auto" }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {checkFailing && (
                <Alert severity="warning">
                  <AlertTitle sx={{ mb: 0.5 }}>
                    Status check failing, deposit/withdrawal status may be
                    outdated
                  </AlertTitle>
                  Failed {entity.exchangeStatus.checkErrorCount} times in a row.
                  Last successful check:{" "}
                  {formatSince(entity.exchangeStatus.lastSuccessfulCheckAt)}.
                  {entity.exchangeStatus.lastCheckError && (
                    <Box
                      component="code"
                      sx={{
                        display: "block",
                        mt: 1,
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {entity.exchangeStatus.lastCheckError}
                    </Box>
                  )}
                  {userData.isAdmin && (
                    <Box sx={{ mt: 1.5, display: "flex" }}>
                      <PauseCheckButton
                        slug={entity.slug}
                        name={entity.name}
                        paused={false}
                        onChange={handleChecksPausedChange}
                      />
                    </Box>
                  )}
                </Alert>
              )}
              {checksPaused && (
                <Alert severity="info">
                  <AlertTitle sx={{ mb: 0.5 }}>
                    Status check paused, deposit/withdrawal status is not
                    monitored
                  </AlertTitle>
                  Paused {formatSince(entity.exchangeStatus.checksPausedAt)}.
                  {entity.exchangeStatus.lastCheckError &&
                    " Last error before pausing:"}
                  {entity.exchangeStatus.lastCheckError && (
                    <Box
                      component="code"
                      sx={{
                        display: "block",
                        mt: 1,
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {entity.exchangeStatus.lastCheckError}
                    </Box>
                  )}
                  {userData.isAdmin && (
                    <Box sx={{ mt: 1.5, display: "flex" }}>
                      <PauseCheckButton
                        slug={entity.slug}
                        name={entity.name}
                        paused
                        onChange={handleChecksPausedChange}
                      />
                    </Box>
                  )}
                </Alert>
              )}
              <UptimeStrip
                title="Deposits"
                type="deposit"
                statusChanges={entity.exchangeStatus.statusChanges}
                checkFailing={checkFailing}
                paused={checksPaused}
                currentUp={
                  currentDepositStatus === "No Data"
                    ? undefined
                    : currentDepositStatus === "Online"
                }
              />
              <UptimeStrip
                title="Withdrawals"
                type="withdrawal"
                statusChanges={entity.exchangeStatus.statusChanges}
                checkFailing={checkFailing}
                paused={checksPaused}
                currentUp={
                  currentWithdrawalStatus === "No Data"
                    ? undefined
                    : currentWithdrawalStatus === "Online"
                }
              />
              <UptimeLegend />
            </Box>
          </CustomCard>
        </Grid>
      )}

      <Grid item xs={12}>
        <CustomCard cardHeader={"Trading volume ($)"} sx={{ height: "auto" }}>
          {dataLineChart && <LineChart data={dataLineChart} />}
        </CustomCard>
      </Grid>
    </Grid>
  );
};

export default ExchangeDetails;
